import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { Instrument } from '@/lib/schema/types';
import type { ObservationRecord } from '@/lib/state/types';
import { scoreInstrument } from '@/lib/scoring/totalScore';
import { scoreItem } from '@/lib/scoring/itemScore';
import { ratingLevel } from '@/lib/schema/loader';

/**
 * Print-ready PDF report of an observation.
 *
 * Layout mirrors the Summary view: cover page with total scale score and
 * observation metadata, then one section per subscale listing items and
 * their computed scores. We use jsPDF + jspdf-autotable to keep the
 * bundle small (no headless Chromium, no @react-pdf/renderer peer-dep
 * pain) and get consistent typography across browsers.
 *
 * The page palette uses the same rating-scale colors that drive the UI
 * so the printed report is visually continuous with what the observer
 * saw on screen.
 */

const DECE_NAVY: [number, number, number] = [31, 43, 91];
const DECE_GOLD: [number, number, number] = [198, 162, 74];

function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return [r, g, b];
}

function formatScore(score: number | null | 'NA'): string {
  if (score === 'NA') return 'NA';
  if (score == null) return '—';
  return String(score);
}

export function exportObservationPdf(
  instrument: Instrument,
  record: ObservationRecord,
): void {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 48;

  const total = scoreInstrument(instrument, record.responses, record.itemNa);

  // ── Header band ─────────────────────────────────────────────────────
  doc.setFillColor(...DECE_NAVY);
  doc.rect(0, 0, pageWidth, 72, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('FCCERS-3 Observation Report', margin, 42);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(instrument.full_name, margin, 60);

  // ── Metadata block ──────────────────────────────────────────────────
  doc.setTextColor(0, 0, 0);
  let y = 100;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Observation details', margin, y);
  y += 18;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);

  const metaLines: Array<[string, string]> = [
    ['Observer', record.metadata.observerName || '—'],
    ['Email', record.metadata.observerEmail || '—'],
    ['Provider', record.metadata.providerName || '—'],
    ['Site', record.metadata.siteName || '—'],
    ['Address', record.metadata.siteAddress || '—'],
    ['Date', record.metadata.observationDate || '—'],
    [
      'Time',
      record.metadata.startTime && record.metadata.endTime
        ? `${record.metadata.startTime} – ${record.metadata.endTime}`
        : '—',
    ],
    ['Children present', String(record.metadata.childrenPresent ?? '—')],
    [
      'Age groups',
      record.metadata.ageGroupsPresent.length
        ? record.metadata.ageGroupsPresent.join(', ')
        : '—',
    ],
  ];
  const colWidth = (pageWidth - margin * 2) / 2;
  metaLines.forEach(([label, value], idx) => {
    const col = idx % 2;
    const row = Math.floor(idx / 2);
    const rowY = y + row * 16;
    const colX = margin + col * colWidth;
    doc.setFont('helvetica', 'bold');
    doc.text(`${label}:`, colX, rowY);
    doc.setFont('helvetica', 'normal');
    doc.text(value, colX + 90, rowY);
  });
  y += Math.ceil(metaLines.length / 2) * 16 + 12;

  if (record.metadata.notes) {
    doc.setFont('helvetica', 'bold');
    doc.text('Session notes:', margin, y);
    y += 14;
    doc.setFont('helvetica', 'normal');
    const wrapped = doc.splitTextToSize(
      record.metadata.notes,
      pageWidth - margin * 2,
    );
    doc.text(wrapped, margin, y);
    y += wrapped.length * 12 + 8;
  }

  // ── Total score panel ───────────────────────────────────────────────
  doc.setDrawColor(...DECE_GOLD);
  doc.setLineWidth(1.5);
  doc.rect(margin, y, pageWidth - margin * 2, 60);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...DECE_NAVY);
  doc.text('Total scale score', margin + 14, y + 22);
  doc.setFontSize(28);
  doc.text(
    total.average != null ? total.average.toFixed(2) : '—',
    margin + 14,
    y + 50,
  );
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  const summary = `${total.scoredItems} scored · ${total.naItems} NA · ${total.pendingItems} pending`;
  doc.text(summary, pageWidth - margin - 14, y + 50, { align: 'right' });
  y += 80;

  // ── Per-subscale tables ─────────────────────────────────────────────
  for (const s of total.subscales) {
    if (y > 680) {
      doc.addPage();
      y = margin;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...DECE_NAVY);
    doc.text(s.name, margin, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text(
      s.result.average != null
        ? `Subscale average: ${s.result.average.toFixed(2)}`
        : 'Subscale average: —',
      pageWidth - margin,
      y,
      { align: 'right' },
    );
    y += 10;

    const rows = s.result.itemResults.map(({ item, score, itemNa }) => {
      const r = scoreItem(item, record.responses, {
        itemNa: record.itemNa[item.id] ?? false,
      });
      const display = itemNa ? 'NA' : formatScore(score);
      const label = ratingLevel(Math.round(score ?? 0))?.label ?? '';
      const notes = record.itemNotes[item.id] ?? '';
      return [
        String(item.number),
        item.name,
        display,
        itemNa || score == null ? '' : label,
        r.fullySwept ? '✓' : r.allIndicatorsNa ? 'all NA' : 'partial',
        notes,
      ];
    });

    autoTable(doc, {
      startY: y + 6,
      head: [['#', 'Item', 'Score', 'Rating', 'Sweep', 'Notes']],
      body: rows,
      theme: 'striped',
      styles: { fontSize: 9, cellPadding: 5 },
      headStyles: { fillColor: DECE_NAVY, textColor: 255 },
      columnStyles: {
        0: { cellWidth: 28, halign: 'right' },
        2: { cellWidth: 40, halign: 'center', fontStyle: 'bold' },
        3: { cellWidth: 80 },
        4: { cellWidth: 52, halign: 'center' },
        5: { cellWidth: 'auto' },
      },
      // Score-cell background uses the rating color so the table reads
      // like the on-screen tiles.
      didParseCell: (data) => {
        if (data.section !== 'body' || data.column.index !== 2) return;
        const raw = data.cell.raw;
        if (typeof raw !== 'string' || raw === '—' || raw === 'NA') return;
        const num = Number(raw);
        if (!Number.isFinite(num)) return;
        const color = ratingLevel(num)?.color_hex;
        if (color) {
          data.cell.styles.fillColor = hexToRgb(color);
          data.cell.styles.textColor = 255;
        }
      },
    });
    // @ts-expect-error — jspdf-autotable augments the doc with `.lastAutoTable`.
    y = (doc.lastAutoTable?.finalY ?? y) + 20;
  }

  // ── Footer on every page ───────────────────────────────────────────
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i += 1) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(
      `Generated ${new Date(record.updatedAt).toLocaleString()} · Schema v${record.schemaVersion} · Powered by SOLVED`,
      margin,
      doc.internal.pageSize.getHeight() - 24,
    );
    doc.text(
      `Page ${i} / ${totalPages}`,
      pageWidth - margin,
      doc.internal.pageSize.getHeight() - 24,
      { align: 'right' },
    );
  }

  const filename = `fccers-${(record.metadata.siteName || 'observation')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}-${record.metadata.observationDate || 'draft'}.pdf`;
  doc.save(filename);
}
